/**
 * Layout principal de l'application après connexion.
 * 
 * Gère les onglets (Chat, Tables, Game, Settings) avec display:none
 * pour maintenir les composants montés et préserver leur état/sockets.
 * Le composant Game est monté conditionnellement (seulement quand une partie est en cours).
 */

import { useCallback, useEffect, useState } from 'react';
import { AppProvider, useApp } from '../contexts/AppContext';
import { ProfileProvider, useProfile } from '../contexts/ProfileContext';
import NavSidebar from './NavSidebar';
import AppToastContainer from './AppToast';
import { InvitationToastContainer } from './InvitationToast';
import { DefiTableAlertContainer } from './DefiTableAlertToast';
import PlayerProfileModal from './PlayerProfileModal';
import TeamProfileModal from './TeamProfileModal';
import CompetitionUpsellBanner from './CompetitionUpsellBanner';
import SystemStatusBanner from './SystemStatusBanner';
import BetaFeedbackButton from './BetaFeedbackButton';
import BetaWelcomeModal from './BetaWelcomeModal';
import Home from '../pages/Home';
import Chat from '../pages/Chat';
import Tables from '../pages/Tables';
import Game from '../pages/Game';
import Settings from '../pages/Settings';
import Rankings from '../pages/Rankings';
import CompetitionsPage from '../pages/CompetitionsPage';
import TrainingPage from '../pages/TrainingPage';
import LibraryPage from '../pages/LibraryPage';
import ForumsPage from '../pages/ForumsPage';
import AdminPage from '../pages/AdminPage';
import ModerationPage from '../pages/admin/ModerationPage';
import Subscription from '../pages/Subscription';
import MySubscription from '../pages/MySubscription';
import { useNotificationSocket, type DefiTableAlert } from '../hooks/useNotificationSocket';
import { usePageViewTracking } from '../hooks/usePageViewTracking';
import { dispatchLibraryNewShare, useLibraryNotifications } from '../hooks/useLibraryNotifications';
import { parseLibraryDeepLink } from '../utils/libraryDeepLink';
import { parseDiffereDeepLink } from '../utils/differeDeepLink';
import { formatLibraryShareToastMessage } from '../utils/libraryLabels';
import type { LibraryNewShareEvent } from '../types/library-notifications';
import { useResponsive } from '../hooks/useResponsive';
import type { Session } from '../types/app';
import { BACKEND_URL } from '../config';
import { setNativeBackground } from '../platform/capacitor';
import '../styles/layout.css';

interface MainLayoutProps {
  session: Session;
  onLogout: () => void;
  onToggleCompet?: () => void;
  onSessionUpdate?: (session: Session) => void;
}

/** Contenu interne qui a accès au contexte */
function MainLayoutContent({ session, onLogout, onToggleCompet, onSessionUpdate }: MainLayoutProps) {
  const {
    activeTab, gameInfo, setActiveTab, showToast, preferences, setInvitationCount,
    setLibraryNewCount, setLibraryPendingPollCount,
    navigateToLibrary,
    navigateToDiffere,
    startGame,
    expectedDefiTableKeys,
    addExpectedDefiTable,
    removeExpectedDefiTable,
    trainingFeltActive,
  } = useApp();
  const { isWide, isCompact, isLandscape } = useResponsive();
  const onFeltTable = (activeTab === 'game' && Boolean(gameInfo))
    || (activeTab === 'training' && trainingFeltActive);
  const hideNavOnFeltLandscape = isCompact && isLandscape && onFeltTable;

  useEffect(() => {
    void setNativeBackground(onFeltTable ? 'felt' : 'app');
  }, [onFeltTable, preferences.theme]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canModerate, setCanModerate] = useState(false);

  // Check admin / moderator status on mount
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/check?pseudo=${encodeURIComponent(session.pseudo)}`)
      .then(r => r.json())
      .then(data => setIsAdmin(data.isAdmin === true))
      .catch(() => {});
    fetch(`${BACKEND_URL}/api/moderation/check?pseudo=${encodeURIComponent(session.pseudo)}`)
      .then(r => r.json())
      .then(data => setCanModerate(data.canModerate === true))
      .catch(() => {});
  }, [session.pseudo]);

  // Rediriger vers 'tables' si on est sur 'home' et que le viewport n'est plus wide
  useEffect(() => {
    if (activeTab === 'home' && !isWide) {
      setActiveTab('tables');
    }
  }, [activeTab, isWide, setActiveTab]);

  const handleInvitationAccepted = useCallback((_draftId: string) => {
    setActiveTab('tables');
  }, [setActiveTab]);

  const handleLibraryNewShare = useCallback((data: LibraryNewShareEvent) => {
    dispatchLibraryNewShare(data);

    if (!data.sharedBy) return;

    showToast({
      message: formatLibraryShareToastMessage(data),
      type: 'system',
      icon: '📚',
      duration: 8000,
      actionLabel: 'Voir',
      libraryNav: data.isPoll
        ? {
            section: 'shared_private',
            sharedGameId: data.sharedGameId,
            mode: 'poll',
          }
        : {
            section: 'shared_private',
            sharedGameId: data.sharedGameId,
            highlightOnly: true,
          },
    }, { sourceTab: 'library' });
  }, [showToast]);

  const handleDefiTableReady = useCallback((alert: DefiTableAlert) => {
    addExpectedDefiTable(alert.tableKey);
  }, [addExpectedDefiTable]);

  const handleForceJoinDefiTable = useCallback((alert: DefiTableAlert) => {
    startGame({ ip: alert.ip, port: alert.port, idserv: alert.idserv });
    removeExpectedDefiTable(alert.tableKey);
    showToast({
      message: `Connexion automatique — ${alert.defiKindLabel || 'votre défi'} commence`,
      subtitle: 'Vous n\'aviez pas encore rejoint la table, on vous a connecté avant le début de la partie.',
      type: 'game',
      duration: 7000,
    });
  }, [startGame, removeExpectedDefiTable, showToast]);

  const {
    pendingInvitations,
    respondToInvitation,
    removeInvitation,
    pendingDefiTableAlerts,
    allDefiTableAlerts,
    removeDefiTableAlert,
    snoozeDefiTableAlert,
  } = useNotificationSocket({
    pseudo: session.pseudo,
    compet: session.compet,
    hasCompetitionAccess: session.hasCompetitionAccess,
    onInvitationAccepted: handleInvitationAccepted,
    gameInfo,
    preferences,
    onShowToast: showToast,
    onLibraryNewShare: session.compet ? handleLibraryNewShare : undefined,
    onDefiTableReady: handleDefiTableReady,
    onForceJoinDefiTable: handleForceJoinDefiTable,
  });

  const handleJoinDefiTable = useCallback((alert: DefiTableAlert) => {
    startGame({ ip: alert.ip, port: alert.port, idserv: alert.idserv });
    removeDefiTableAlert(alert.tableKey);
    removeExpectedDefiTable(alert.tableKey);
  }, [startGame, removeDefiTableAlert, removeExpectedDefiTable]);

  // Filet de sécurité : si l'utilisateur a rejoint la table par un autre biais
  // (clic direct dans Tables.tsx, connexion externe...) ou si la table a
  // disparu, `expectedDefiTableKeys` ne la contient plus — on annule alors le
  // toast et la connexion automatique programmée pour cette table.
  useEffect(() => {
    for (const alert of allDefiTableAlerts) {
      if (!expectedDefiTableKeys.has(alert.tableKey)) {
        removeDefiTableAlert(alert.tableKey);
      }
    }
  }, [expectedDefiTableKeys, allDefiTableAlerts, removeDefiTableAlert]);

  usePageViewTracking(session.pseudo, session.token, activeTab, session.compet);

  useLibraryNotifications({
    pseudo: session.pseudo,
    compet: session.compet ?? false,
    activeTab,
    onSummary: (summary) => {
      setLibraryNewCount(summary.newShareCount);
      setLibraryPendingPollCount(summary.pendingPollCount);
    },
  });

  useEffect(() => {
    if (!session.compet) return;
    const differeLink = parseDiffereDeepLink();
    if (differeLink) {
      navigateToDiffere(differeLink);
      return;
    }
    const link = parseLibraryDeepLink();
    if (link) navigateToLibrary(link);
  }, [session.compet, navigateToLibrary, navigateToDiffere]);

  useEffect(() => {
    setInvitationCount(pendingInvitations.length);
  }, [pendingInvitations.length, setInvitationCount]);

  return (
    <div className={`main-layout ${isCompact ? 'main-layout--compact' : ''}${hideNavOnFeltLandscape ? ' main-layout--game-immersive' : ''}`}>
      <NavSidebar session={session} onLogout={onLogout} onToggleCompet={onToggleCompet} isAdmin={isAdmin || canModerate} />

      <div className="main-content">
        <SystemStatusBanner />
        <BetaFeedbackButton session={session} />
        <BetaWelcomeModal />
        {/* Onglet Accueil — toujours monté */}
        <div
          className="tab-panel"
          style={{ display: activeTab === 'home' ? 'flex' : 'none' }}
        >
          <Home session={session} />
        </div>

        {/* Onglet Discussions — toujours monté */}
        <div
          className="tab-panel"
          style={{ display: activeTab === 'chat' ? 'flex' : 'none' }}
        >
          <Chat session={session} isAdmin={isAdmin} canModerate={canModerate || isAdmin} />
        </div>

        {/* Onglet Tables — toujours monté */}
        <div
          className="tab-panel"
          style={{ display: activeTab === 'tables' ? 'flex' : 'none' }}
        >
          <Tables session={session} isAdmin={isAdmin} />
        </div>

        {/* Onglet Partie — monté uniquement quand une partie est en cours */}
        {gameInfo && (
          <div
            className="tab-panel"
            style={{ display: activeTab === 'game' ? 'flex' : 'none' }}
          >
            <Game session={session} gameInfo={gameInfo} />
          </div>
        )}

        {/* Onglet Classements — toujours monté */}
        <div
          className="tab-panel"
          style={{ display: activeTab === 'rankings' ? 'flex' : 'none' }}
        >
          <Rankings session={session} />
        </div>

        {/* Onglet Compétitions — monté uniquement en compétition */}
        {session.compet && (
          <div
            className="tab-panel"
            style={{ display: activeTab === 'competitions' ? 'flex' : 'none' }}
          >
            {!session.hasCompetitionAccess && <CompetitionUpsellBanner />}
            <CompetitionsPage session={session} isAdmin={isAdmin} />
          </div>
        )}

        {/* Onglet Entraînement — monté uniquement en compétition */}
        {session.compet && (
          <div
            className="tab-panel"
            style={{ display: activeTab === 'training' ? 'flex' : 'none' }}
          >
            {!session.hasCompetitionAccess && <CompetitionUpsellBanner />}
            <TrainingPage session={session} isAdmin={isAdmin} />
          </div>
        )}

        {/* Onglet Bibliothèque — monté uniquement en compétition */}
        {session.compet && (
          <div
            className="tab-panel"
            style={{ display: activeTab === 'library' ? 'flex' : 'none' }}
          >
            {!session.hasCompetitionAccess && <CompetitionUpsellBanner />}
            <LibraryPage session={session} isAdmin={isAdmin} />
          </div>
        )}

        {/* Onglet Forums — toujours monté */}
        <div
          className="tab-panel"
          style={{ display: activeTab === 'forums' ? 'flex' : 'none' }}
        >
          <ForumsPage session={session} />
        </div>

        {/* Onglet Admin — monté si admin tech ou modérateur legacy */}
        {(isAdmin || canModerate) && (
          <div
            className="tab-panel"
            style={{ display: activeTab === 'admin' ? 'flex' : 'none' }}
          >
            {isAdmin ? (
              <AdminPage session={session} />
            ) : (
              <div className="admin-page">
                <div className="admin-page-header">
                  <h1>Modération chat</h1>
                </div>
                <ModerationPage session={session} section="active" />
              </div>
            )}
          </div>
        )}

        {/* Onglet Paramètres — toujours monté */}
        <div
          className="tab-panel"
          style={{ display: activeTab === 'settings' ? 'flex' : 'none' }}
        >
          <Settings session={session} onSessionUpdate={onSessionUpdate} />
        </div>

        {/* Onglet Abonnement — toujours monté */}
        <div
          className="tab-panel"
          style={{ display: activeTab === 'subscription' ? 'flex' : 'none' }}
        >
          <Subscription session={session} />
        </div>

        {/* Onglet Mon abonnement — toujours monté */}
        <div
          className="tab-panel"
          style={{ display: activeTab === 'mySubscription' ? 'flex' : 'none' }}
        >
          <MySubscription session={session} />
        </div>
      </div>

      {/* Toasts applicatifs en superposition */}
      <AppToastContainer />

      {/* Toasts d'invitation en superposition */}
      <InvitationToastContainer
        invitations={pendingInvitations}
        onRespond={respondToInvitation}
        onDismiss={removeInvitation}
      />

      {/* Alertes tables de défis legacy (CIA / défi libre / défi par équipe) */}
      <DefiTableAlertContainer
        alerts={pendingDefiTableAlerts}
        onJoin={handleJoinDefiTable}
        onDismiss={snoozeDefiTableAlert}
      />

      {/* Fiches joueur / équipe */}
      <ProfileModals session={session} />
    </div>
  );
}

function ProfileModals({ session }: { session: Session }) {
  const { target, close, openTeamProfile } = useProfile();
  if (!target) return null;

  if (target.type === 'player') {
    return (
      <PlayerProfileModal
        pseudo={target.id}
        compet={session.compet}
        onClose={close}
        onOpenTeam={(teamId) => openTeamProfile(teamId)}
      />
    );
  }

  return (
    <TeamProfileModal
      teamId={target.id}
      compet={session.compet}
      onClose={close}
    />
  );
}

/** Wrapper qui fournit le AppContext + ProfileContext */
export default function MainLayout({ session, onLogout, onToggleCompet, onSessionUpdate }: MainLayoutProps) {
  return (
    <AppProvider>
      <ProfileProvider>
        <MainLayoutContent
          session={session}
          onLogout={onLogout}
          onToggleCompet={onToggleCompet}
          onSessionUpdate={onSessionUpdate}
        />
      </ProfileProvider>
    </AppProvider>
  );
}
